import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrency, showError } from 'utils/utils';
import { faCheck, faDollarSign, faMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import {
  DynamicFlight,
  FareFamilyDescription,
  FareFamilyInfo,
} from 'ts/dynamic/interfaces/DynamicResponse';
import { changeFlex } from 'services/dynamic';
import useRecaptcha from 'hooks/useRecaptcha';
import { useSearchParams } from 'react-router-dom';
import { formatPriceDynamic } from 'utils/dynamic/dynamic';
import { DynamicContext } from 'contexts/DynamicContext';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface DynamicFlexDetailsProps {
  flexFlights: DynamicFlight[];
  onChooseFlex: (totalPrice: number, flight: DynamicFlight) => void;
  selectedFlight: DynamicFlight;
}

const DynamicFlexDetails: FC<DynamicFlexDetailsProps> = (props) => {
  const [t] = useTranslation();
  const [description, setDescription] = useState<FareFamilyDescription[]>([]);
  const [urlParams] = useSearchParams();
  const { numberOfPax } = useContext(DynamicContext);
  const [info, setInfo] = useState<FareFamilyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { handleRecapcha } = useRecaptcha();
  const { currency } = useContext(DynamicContext);
  const lang = localStorage.getItem('language') || 'he';

  useEffect(() => {
    const newInfo = props.flexFlights
      .filter((flight) => {
        if (flight.inbound && flight.outbound && flight.fareFamilyInfo.length > 1) {
          return flight.fareFamilyInfo[0].desc === flight.fareFamilyInfo[1].desc;
        } else if (flight.outbound) {
          return flight.fareFamilyInfo;
        } else {
          return false;
        }
      })
      .map((flight) => ({
        ...flight.fareFamilyInfo[0],
        price: flight.flighPrice.grossAndTax[currency] as number,
        flightId: flight.id,
      }));
    if (newInfo.length && props.selectedFlight.id) {
      const index = newInfo.findIndex((inf) => inf.flightId === props.selectedFlight.id);
      newInfo[index === -1 ? 0 : index].checked = true;
    }

    const descriptionList = newInfo.map((flight) => flight.fareFamilyDescription);
    const newDescription = descriptionList
      .flat()
      .filter((item, index, self) => {
        return index === self.findIndex((t) => t.desc === item.desc);
      })
      .filter((item) => item.sysCode > 1);
    setInfo(newInfo);

    setDescription(newDescription);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getFlexCell = (desc: string, column: FareFamilyInfo) => {
    const description = column.fareFamilyDescription.find((flight) => {
      return flight.desc === desc;
    });

    if (description?.code === 'CHA') {
      return {
        icon: faDollarSign,
        color: 'text-dark',
      };
    } else if (description?.code === 'INC') {
      return {
        icon: faCheck,
        color: 'text-success',
      };
    } else {
      return {
        icon: faMinus,
        color: 'danger-color',
      };
    }
  };

  const onClickFlex = async (value: boolean, index: number) => {
    const newInfo = [...info];
    newInfo.forEach((item) => (item.checked = false));
    newInfo[index].checked = value;
    setInfo(newInfo);
    setIsLoading(true);

    const token = await handleRecapcha();
    if (token) {
      const res = await changeFlex(token, newInfo[index].flightId, urlParams.get('searchToken')!);

      if (res?.hasErrors || res?.sessionExpired) {
        console.log('Dynamic/SetFlex ' + res.errorMessage);
        showError(t('something_went_wrong'));
      } else if (res?.flexFlightChanged) {
        const selectedFlight = props.flexFlights.find((fl) => fl.id === newInfo[index].flightId)!;
        props.onChooseFlex(res.packageTotalPrice, selectedFlight);
      }
    }

    setIsLoading(false);
  };

  return (
    <Fragment>
      {!!info.length && (
        <div className="mt-30">
          <div className="title-underline text-lg mb-20">{t('flight.updgrade')}</div>

          <div className="flex-table">
            <div
              className="flex-table-col names"
              style={{ flex: `0 0 ${100 / (info.length + 1)}%` }}
            >
              <div className="flex-table-cell">
                <div className="text-bold text-st">{t('hotel.description')}</div>
              </div>
              {description.map((row, rowIndex) => (
                <div className="flex-table-cell" key={rowIndex}>
                  {lang === 'he' && (row.descLang || row.desc).toLowerCase()}
                  {lang === 'en' && row.desc.toLowerCase()}
                </div>
              ))}
              <div className="flex-table-cell"></div>
            </div>

            {info.map((column, columnIndex) => (
              <div
                className={'flex-table-col' + (column.checked ? ' checked' : '')}
                key={columnIndex}
                style={{ flex: `0 0 ${100 / (info.length + 1)}%` }}
              >
                <div className="flex-table-cell head text-bold">{column.desc}</div>
                {description.map((row, rowIndex) => (
                  <div className="flex-table-cell" key={rowIndex}>
                    <FontAwesomeIcon
                      className={getFlexCell(row.desc, column).color}
                      size="xl"
                      icon={getFlexCell(row.desc, column).icon}
                    />
                  </div>
                ))}
                <span className="flex-table-cell text-bold text-md">
                  <Checkbox
                    checked={column.checked}
                    className="mr-5"
                    onlyChecked={true}
                    onChange={(value) => onClickFlex(value, columnIndex)}
                    disabled={isLoading}
                  />
                  <span className="text-nowrap">
                    {formatPriceDynamic(
                      (column.price -
                        (props.selectedFlight.flighPrice.grossAndTax[currency] as number)) /
                        numberOfPax,
                      getCurrency(currency)
                    )}
                  </span>

                  <span className="text-light text-xsm ml-5 text-normal">
                    {t('dyn.per_passenger')}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(DynamicFlexDetails, 'DynamicFlexDetails');
